import { useContext } from 'react';
import { CollapsiblePanel, FilterGroup, Icon } from '../../components';
import './Filters.scss';
import { SettingsContext } from '../../context';
import { CLASSES, SCHOOLS, SOURCES, COMPONENTS, DAMAGE_TYPES, ABILITIES } from '../../data';

export const Filters = ({ filters, onChange }) => {
    const settings = useContext(SettingsContext);

    const handleFilterChange = (key, value) => {
        onChange({
            ...filters,
            [key]: value
        });
    };

    const handleClearFilters = () => {
        onChange({});
    };

    const handleClassFilterChange = (value) => handleFilterChange('class', value);
    const handleSchoolFilterChange = (value) => handleFilterChange('school', value);
    const handleLevelFilterChange = (value) => handleFilterChange('level', value);
    const handleSourceFilterChange = (value) => handleFilterChange('source', value);
    const handleComponentFilterChange = (value) => handleFilterChange('component', value);
    const handleDamageTypeFilterChange = (value) => handleFilterChange('damageType', value);
    const handleSaveFilterChange = (value) => handleFilterChange('save', value);
    const handleTagFilterChange = (value) => handleFilterChange('tag', value);
    const handleAttackFilterChange = (value) => handleFilterChange('attack', value);
    const handleCastingTimeFilterChange = (value) => handleFilterChange('castingTime', value);

    const getFilterCount = () => (
        (filters.class ? 1 : 0)
        + (!!filters.school ? 1 : 0)
        + (!!filters.save ? 1 : 0)
        + (!!filters.attack ? 1 : 0)
        + (!!filters.castingTime ? 1 : 0)
        + (filters.level?.length || 0)
        + (filters.source?.length || 0)
        + (filters.damageType?.length || 0)
        + (filters.component?.positive?.length || 0)
        + (filters.component?.negative?.length || 0)
        + (filters.tag?.positive?.length || 0)
        + (filters.tag?.negative?.length || 0)
    );

    const filterSections = [
        { visible: settings.showClassFilter, label: 'Class', content: (
            <FilterGroup 
                options={CLASSES}
                value={filters?.class}
                onChange={handleClassFilterChange}
            />
        )},
        { visible: settings.showSchoolFilter, label: 'School', content: (
            <FilterGroup 
                options={Object.keys(SCHOOLS)}
                renderOption={o => SCHOOLS[o].slice(0,4)}
                columns={4}
                value={filters?.school}
                onChange={handleSchoolFilterChange}
            />
        )},
        { visible: settings.showLevelFilter, label: 'Level', content: (
            <FilterGroup 
                options={(new Array(10).fill(0).map((_, i) => i))}
                columns={5}
                multiselect
                value={filters?.level}
                onChange={handleLevelFilterChange}
            />
        )},
        { visible: settings.showSourceFilter, label: 'Source', content: (
            <FilterGroup 
                options={SOURCES}
                columns={7}
                multiselect
                onChange={handleSourceFilterChange}
                value={filters?.source}
            />
        )},
        { visible: settings.showComponentFilter, label: 'Components', content: (
            <FilterGroup 
                options={Object.keys(COMPONENTS)}
                renderOption={o => COMPONENTS[o]}
                columns={5}
                multiselect
                onChange={handleComponentFilterChange}
                value={filters?.component}
                enableNegatives
            />
        )},
        { visible: settings.showDamageTypeFilter, label: 'Damage', content: (
            <FilterGroup 
                options={DAMAGE_TYPES}
                columns={5}
                multiselect
                value={filters?.damageType}
                onChange={handleDamageTypeFilterChange}
            />
        )},
        { visible: settings.showSaveFilter, label: 'Save', content: (
            <FilterGroup 
                options={ABILITIES}
                renderOption={o => `${o.slice(0,3).toUpperCase()} save`}
                columns={3}
                onChange={handleSaveFilterChange}
                value={filters?.save}
            />
        )},
        { visible: settings.showTagsFilter, label: 'Tags', content: (
            <FilterGroup 
                options={['RITUAL', 'CONCENTRATION']}
                columns={2}
                multiselect
                value={filters?.tag}
                onChange={handleTagFilterChange}
                enableNegatives
            />
        )},
        { visible: settings.showAttackFilter, label: 'Attack', content: (
            <FilterGroup 
                options={['Melee Atk', 'Ranged Atk']}
                columns={2}
                value={filters?.attack}
                onChange={handleAttackFilterChange}
            />
        )},
        { visible: settings.showCastingTimeFilter, label: 'Casting Time', content: (
            <FilterGroup 
                options={['ACTION', 'BONUS', 'REACTION', 'OTHER']}
                columns={4}
                value={filters?.castingTime}
                onChange={handleCastingTimeFilterChange}
            />
        )},
    ].filter(section => section.visible);

    return (
        <CollapsiblePanel 
            className="Filters"
            note={!!getFilterCount() && (
                <>
                    {getFilterCount()} active filter{getFilterCount() === 1 ? '' : 's'}
                    <Icon name='times' size='small' style={{marginLeft: '.5rem'}} />
                </>
            )}
            onNoteClick={handleClearFilters}
        >
            {filterSections.map(({ label, content }) => (
                <div key={label} className="FilterSection">
                    <div className="FilterSectionLabel">{label}</div>
                    {content}
                    <hr />
                </div>
            ))}
        </CollapsiblePanel>
    );
};